import { useCallback } from 'react';

import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';

/**
 * The active-policy match requestBind warns on, looked up before Staff opens the offer sharing
 * options (#34591) so the modal can open with its callout already in place. Resolves the matching
 * policy ids, or null when there is no match, no option to check, or the check fails (the
 * bind-time check still stands).
 */
const useActivePolicyMatch = () => {
  const { offer: store } = useStore();
  const {
    user: { username },
    isAgency
  } = useSession();

  return useCallback(
    async (policyType) => {
      const option = store.offer.options?.find((o) => o.type === policyType);
      if (!option) {
        return null;
      }
      try {
        const match = await store.checkMatchingActivePolicies(option.id);
        if (!match?.foundMatchingActivePolicy) {
          return null;
        }
        track('Offer sharing options - matched active policy', {
          user: username,
          offerId: store.offer.id,
          isAgency,
          optionId: option.id,
          matchingPolicyIds: match.matchingPolicyIds
        });
        return match.matchingPolicyIds ?? [];
      } catch (error) {
        track('Offer sharing options - active-policy check failed', {
          user: username,
          offerId: store.offer.id,
          isAgency,
          error: error.message
        });
        return null;
      }
    },
    [store, username, isAgency]
  );
};

export default useActivePolicyMatch;
